import React from 'react';
import { useTranslation } from '../hooks/useTranslation';
import SEOHead from '../components/SEOHead';

const CGU = () => {
  const { t } = useTranslation();

  return (
    <>
      <SEOHead 
        title="Conditions Générales d'Utilisation - Golden Mali Suite"
        description="Conditions générales d'utilisation de la plateforme Golden Mali Suite"
      />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-navy-800 mb-6">
            Conditions Générales d'Utilisation
          </h1>
          
          <div className="prose max-w-none text-gray-700 space-y-6">
            <p className="text-sm text-gray-500 mb-8">
              <strong>Dernière mise à jour :</strong> Février 2026
            </p>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">1. Acceptation des Conditions</h2>
              <p>
                Les présentes Conditions Générales d'Utilisation (CGU) régissent l'accès et l'utilisation 
                de la plateforme SaaS <strong>Golden Mali Suite</strong>, édité par <strong>GOLDEN MALI SARL</strong>.
              </p>
              <p>
                En accédant et en utilisant la plateforme, vous acceptez sans réserve les présentes CGU. 
                Si vous n'acceptez pas ces conditions, vous ne devez pas utiliser la plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">2. Informations Légales</h2>
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <p><strong>Éditeur :</strong> GOLDEN MALI SARL</p>
                <p><strong>Forme juridique :</strong> Société à Responsabilité Limitée (SARL)</p>
                <p><strong>Gérant :</strong> FOFANA Ibrahima</p>
                <p><strong>Adresse du siège social :</strong> Imm DJOGUIBA, 2ème étage, bureau C4, Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>NINA :</strong> 42009193276098T</p>
                <p><strong>RCCM :</strong> MA.BKO.2020.B.7764</p>
                <p><strong>Site web :</strong> https://golden-mali.cloud</p>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>Développement et Maintenance :</strong> Créatif-web</p>
                <p><strong>Statut :</strong> Entrepreneur Individuel (EI)</p>
                <p><strong>Adresse :</strong> 16 rue Vendôme, 13500 Martigues, France</p>
                <p><strong>Téléphone :</strong> 06.10.56.25.06</p>
                <p><strong>Email :</strong> contact@creatifboost.com</p>
                <p><strong>Site web :</strong> https://creatif-web.eu</p>
                <p><strong>SIRET :</strong> 93466266900019</p>
                <p><strong>SIREN :</strong> 934662669</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">3. Définitions</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong>Plateforme :</strong> Désigne le service SaaS Golden Mali Suite accessible via https://golden-mali.cloud</li>
                <li><strong>Utilisateur :</strong> Toute personne autorisée à accéder et utiliser la plateforme</li>
                <li><strong>Compte :</strong> L'espace personnel de l'utilisateur sur la plateforme</li>
                <li><strong>Données :</strong> Toutes les informations saisies, stockées ou générées via la plateforme</li>
                <li><strong>Éditeur :</strong> GOLDEN MALI SARL, éditeur de la plateforme</li>
                <li><strong>Développeur :</strong> Créatif-web, responsable du développement et de la maintenance technique de la plateforme</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">3. Accès à la Plateforme</h2>
              <h3 className="text-xl font-semibold text-navy-700 mb-3">3.1 Conditions d'Accès</h3>
              <p>
                L'accès à la plateforme est réservé aux utilisateurs autorisés par GOLDEN MALI SARL. 
                Chaque utilisateur reçoit des identifiants personnels et confidentiels (email et mot de passe).
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">3.2 Obligations de l'Utilisateur</h3>
              <p>L'utilisateur s'engage à :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Fournir des informations exactes et à jour lors de la création de son compte</li>
                <li>Conserver la confidentialité de ses identifiants de connexion</li>
                <li>Ne pas partager ses identifiants avec des tiers</li>
                <li>Informer immédiatement GOLDEN MALI SARL en cas de perte, vol ou utilisation non autorisée de ses identifiants</li>
                <li>Utiliser la plateforme conformément à sa destination et aux présentes CGU</li>
                <li>Respecter les droits de propriété intellectuelle</li>
                <li>Ne pas tenter d'accéder à des zones non autorisées de la plateforme</li>
                <li>Ne pas perturber le fonctionnement de la plateforme</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">4. Utilisation de la Plateforme</h2>
              <h3 className="text-xl font-semibold text-navy-700 mb-3">4.1 Utilisation Autorisée</h3>
              <p>
                La plateforme est destinée à la gestion professionnelle des flux d'or et des transactions 
                liées à l'activité de GOLDEN MALI SARL. L'utilisateur s'engage à utiliser la plateforme 
                uniquement à des fins professionnelles légitimes.
              </p>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">4.2 Utilisations Interdites</h3>
              <p>Il est strictement interdit :</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>D'utiliser la plateforme à des fins illégales ou frauduleuses</li>
                <li>De tenter de contourner les mesures de sécurité</li>
                <li>D'introduire des virus, chevaux de Troie ou tout autre code malveillant</li>
                <li>De copier, reproduire ou revendre tout ou partie de la plateforme</li>
                <li>D'utiliser des robots, scripts automatisés ou tout autre moyen pour accéder à la plateforme</li>
                <li>De porter atteinte à l'image ou à la réputation de GOLDEN MALI SARL</li>
                <li>De collecter des données d'autres utilisateurs sans autorisation</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">5. Disponibilité et Maintenance</h2>
              <p>
                GOLDEN MALI SARL s'efforce d'assurer une disponibilité maximale de la plateforme (24h/24, 7j/7). 
                Cependant, la plateforme peut être temporairement indisponible pour :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Maintenance programmée (avec préavis dans la mesure du possible)</li>
                <li>Mises à jour techniques ou fonctionnelles</li>
                <li>Interventions d'urgence</li>
                <li>Cas de force majeure</li>
              </ul>
              <p>
                GOLDEN MALI SARL ne saurait être tenue responsable des dommages résultant d'une 
                indisponibilité temporaire du service.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">6. Données et Confidentialité</h2>
              <p>
                L'utilisateur est seul responsable des données qu'il saisit dans la plateforme. 
                Il garantit que les données sont exactes, complètes et à jour.
              </p>
              <p>
                GOLDEN MALI SARL met en œuvre des mesures techniques et organisationnelles appropriées 
                pour assurer la sécurité et la confidentialité des données. Pour plus d'informations, 
                consultez notre <a href="/politique-confidentialite" className="text-gold-600 hover:text-gold-700 underline">Politique de Confidentialité</a>.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">7. Propriété Intellectuelle</h2>
              <p>
                L'ensemble des éléments de la plateforme (textes, images, logos, graphiques, logiciels, 
                structure, design) sont la propriété exclusive de GOLDEN MALI SARL ou de ses partenaires 
                et sont protégés par les lois françaises et internationales relatives à la propriété 
                intellectuelle.
              </p>
              <p>
                Toute reproduction, représentation, modification, publication ou adaptation de tout ou 
                partie des éléments de la plateforme, quel que soit le moyen ou le procédé utilisé, est 
                interdite sans autorisation écrite préalable de GOLDEN MALI SARL.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">8. Responsabilité</h2>
              <h3 className="text-xl font-semibold text-navy-700 mb-3">8.1 Responsabilité de l'Éditeur</h3>
              <p>
                GOLDEN MALI SARL s'efforce de fournir des informations exactes et à jour sur la plateforme. 
                Cependant, GOLDEN MALI SARL ne peut garantir l'exactitude, la complétude ou l'actualité 
                des informations diffusées.
              </p>
              <p>
                GOLDEN MALI SARL ne saurait être tenue responsable :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Des dommages directs ou indirects résultant de l'utilisation de la plateforme</li>
                <li>Des pertes de données résultant d'une utilisation incorrecte ou d'un dysfonctionnement</li>
                <li>Des décisions prises sur la base des informations fournies par la plateforme</li>
                <li>Des transactions effectuées entre les utilisateurs et leurs partenaires</li>
                <li>Des dommages résultant d'une indisponibilité temporaire du service</li>
              </ul>
              
              <h3 className="text-xl font-semibold text-navy-700 mb-3 mt-4">8.2 Responsabilité de l'Utilisateur</h3>
              <p>
                L'utilisateur est seul responsable de l'utilisation qu'il fait de la plateforme et des 
                données qu'il y saisit. Il garantit que son utilisation de la plateforme ne porte pas 
                atteinte aux droits de tiers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">9. Résiliation</h2>
              <p>
                GOLDEN MALI SARL se réserve le droit de suspendre ou résilier l'accès d'un utilisateur 
                à la plateforme en cas de :
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Non-respect des présentes CGU</li>
                <li>Utilisation frauduleuse ou illégale de la plateforme</li>
                <li>Atteinte à la sécurité de la plateforme</li>
                <li>Demande de l'utilisateur</li>
              </ul>
              <p>
                En cas de résiliation, l'utilisateur perd immédiatement l'accès à son compte et aux 
                données associées, sous réserve des obligations légales de conservation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">10. Modification des CGU</h2>
              <p>
                GOLDEN MALI SARL se réserve le droit de modifier les présentes CGU à tout moment. 
                Les utilisateurs sont invités à consulter régulièrement cette page pour prendre 
                connaissance des éventuelles modifications.
              </p>
              <p>
                L'utilisation continue de la plateforme après modification des CGU vaut acceptation 
                des nouvelles conditions. Si vous n'acceptez pas les modifications, vous devez cesser 
                d'utiliser la plateforme.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">11. Loi Applicable et Juridiction</h2>
              <p>
                Les présentes CGU sont régies par le droit français, conformément à la loi pour la 
                confiance en l'économie numérique (LCEN).
              </p>
              <p>
                En cas de litige, et à défaut d'accord amiable, le litige sera porté devant les 
                tribunaux français compétents, conformément aux règles de compétence en vigueur.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold text-navy-800 mb-4">12. Contact</h2>
              <p>
                Pour toute question concernant les présentes CGU :
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p><strong>GOLDEN MALI SARL</strong></p>
                <p>Imm DJOGUIBA, 2ème étage, bureau C4</p>
                <p>Grand marché, Bamako, République du Mali</p>
                <p><strong>Téléphone :</strong> +223 20 22 58 56 / +223 66 92 25 45</p>
                <p><strong>Email :</strong> support@golden-mali.com</p>
                <p><strong>Site web :</strong> https://golden-mali.cloud</p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default CGU;
